import { ProcessGeneral } from 'api/endpoints/processes';
import { UPLOAD_FILE_FIELD_NAME } from 'constants/global';
import BaseRepo from 'container/BaseRepo';
import { buildBadRequestApiError } from 'container/helpers/errors';
import { ApiResponseAsync, isApiError } from 'container/models';
import { SearchRequestTyped } from 'container/search';
import {
  AutomatedIndexingClassifierJobDescription,
  AutomatedIndexingMissingFieldTask,
  AutomatedIndexingScenario,
  AutomatedIndexingTask,
  AutomatedIndexingTaskToInvestigateBase,
  AutomatedIndexingTrainingTask,
  BusinessObjectsCollection,
  CreateAutomatedIndexingScenarioRequest,
  CreateAutomatedIndexingTrainingTaskRequest,
  GeminiPage,
  SearchAutomatedIndexingMissingFieldTasksRequest,
  SearchAutomatedIndexingTasksRequest,
  SearchAutomatedIndexingTasksType,
  SearchAutomatedIndexingTrainingTasksRequest,
  TestIndexingResult,
  TestIndexingResultWithScenarioRequest,
  UpdateScenarioRequestType,
} from 'features/automatedIndexing/types';

import { IAutomatedIndexingRepo } from './IAutomatedIndexingRepo';

export function hasAutomatedIndexingLicense(draft?: ProcessGeneral) {
  return draft?._links?.['searchAutomatedIndexingScenarios'] !== undefined;
}

export function canAutomatedIndexingTaskResume(task?: AutomatedIndexingTask) {
  return task?._links?.['resumeTrainingTask'] !== undefined;
}

export class AutomatedIndexingRepo
  extends BaseRepo
  implements IAutomatedIndexingRepo
{
  deleteTrainingTask = (
    task: AutomatedIndexingTrainingTask
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['delete'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "delete" is not available'))
      );

    return this.helpers.handleDelete(link.href);
  };
  searchNotExtractedFieldTasks = async (
    scenario: AutomatedIndexingScenario,
    request: SearchAutomatedIndexingMissingFieldTasksRequest
  ): ApiResponseAsync<AutomatedIndexingMissingFieldTask[]> => {
    const link = scenario._links
      ? scenario._links['searchNotExtractedFieldTasks']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "search not extracted field tasks" is not available')
      );

    const result = await this.helpers.executeSearch<
      AutomatedIndexingMissingFieldTask[]
    >(link, request, { convertRequestToSnakeCase: true });

    if (isApiError(result)) return result;

    if (!result || result.length === 0) return [];

    return result;
  };
  searchTrainingTasks = async (
    scenario: AutomatedIndexingScenario,
    request: SearchAutomatedIndexingTrainingTasksRequest
  ): ApiResponseAsync<AutomatedIndexingTrainingTask[]> => {
    const link = scenario._links
      ? scenario._links['searchTrainingTasks']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "search automated indexing tasks" is not available')
      );

    const result = await this.helpers.executeSearch<
      AutomatedIndexingTrainingTask[]
    >(link, request, { convertRequestToSnakeCase: true });

    if (isApiError(result)) return result;

    if (!result || result.length === 0) return [];

    return result;
  };
  resumeTrainingTask = (
    task: AutomatedIndexingTask
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['resumeTrainingTask'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "resume training task" is not available')
        )
      );

    return this.helpers.handlePostAction(link.href, task);
  };
  deleteAutomatedIndexingTask = (
    task: AutomatedIndexingTask
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['delete'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "delete" is not available'))
      );

    return this.helpers.handleDelete(link.href);
  };
  searchAutomatedIndexingTasks = async (
    draft: ProcessGeneral,
    request: SearchAutomatedIndexingTasksRequest,
    transformToTaskToInvestigate: boolean
  ): ApiResponseAsync<
    AutomatedIndexingTask[] | AutomatedIndexingTaskToInvestigateBase[]
  > => {
    const link = draft._links
      ? draft._links['searchAutomatedIndexingTasks']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "search automated indexing tasks" is not available')
      );

    const result = await this.helpers.executeSearch<AutomatedIndexingTask[]>(
      link,
      request,
      {
        convertRequestToSnakeCase: true,
      }
    );

    if (isApiError(result)) return result;

    if (!result || result.length === 0) return [];

    if (transformToTaskToInvestigate) {
      return result.map((task): AutomatedIndexingTaskToInvestigateBase => ({
        indexingTaskId: task.id,
        indexingTaskName: task.name,
        pages: '',
        createdAt: task.createdAt,
        _links: task._links,
      }));
    }

    return result;
  };
  uploadFile = (
    draft: ProcessGeneral,
    file: File,
    data?: Record<string, any>
  ): ApiResponseAsync<void> => {
    const link = draft._links?.['uploadAutomatedIndexingInputFile'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "upload automated indexing input file" is not available'
          )
        )
      );
    }

    const fileFormData = new FormData();
    fileFormData.append(UPLOAD_FILE_FIELD_NAME, file);

    if (data) fileFormData.append('properties', JSON.stringify(data));

    return this.helpers.handlePost(link.href, fileFormData);
  };
  getBusinessObjectsCollection = (
    scenario: ProcessGeneral
  ): ApiResponseAsync<BusinessObjectsCollection> => {
    const link = scenario._links
      ? scenario._links['getBusinessObjects']
      : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get business objects" is not available')
        )
      );
    }

    return this.helpers.handleGet<BusinessObjectsCollection>(link.href);
  };

  getClassifyResultFromTask = (
    task: AutomatedIndexingTaskToInvestigateBase
  ): ApiResponseAsync<GeminiPage[]> => {
    const link = task._links ? task._links['getClassifyResult'] : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get classify result" is not available')
        )
      );
    }

    return this.helpers.handleGet<GeminiPage[]>(link.href, {
      responseStopTransformPaths: ['attributes'],
    });
  };
  testIndexingResultWithScenario = (
    task: AutomatedIndexingTaskToInvestigateBase,
    request: TestIndexingResultWithScenarioRequest
  ): ApiResponseAsync<TestIndexingResult[]> => {
    const link = task._links
      ? task._links['getIndexingResult'] ||
        task._links['getUnclassifiedDocumentsIndexingResult']
      : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get indexing result" is not available')
        )
      );
    }

    return this.helpers.handlePost<
      TestIndexingResultWithScenarioRequest,
      TestIndexingResult[]
    >(link.href, request, {
      responseStopTransformPaths: ['field_values'],
    });
  };
  searchTasksWithUnclassifiedDocuments = async (
    scenario: AutomatedIndexingScenario,
    request: SearchAutomatedIndexingTasksType
  ): ApiResponseAsync<AutomatedIndexingTaskToInvestigateBase[]> => {
    const link = scenario._links
      ? scenario._links['searchTasksWithUnclassifiedDocuments']
      : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "search tasks with unclassified documents" is not available'
          )
        )
      );
    }

    return this.helpers
      .executeSearch<AutomatedIndexingTaskToInvestigateBase[]>(link, request)
      .then((searchResult) => {
        if (isApiError(searchResult)) {
          return searchResult;
        }
        if (!searchResult) searchResult = [];
        return searchResult;
      });
  };
  searchAutomatedIndexingScenarios = async (
    draft: ProcessGeneral,
    request: Omit<SearchRequestTyped<AutomatedIndexingScenario>, 'count'>
  ): ApiResponseAsync<AutomatedIndexingScenario[]> => {
    const link = draft._links
      ? draft._links['searchAutomatedIndexingScenarios']
      : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "search automated indexing scenarios" is not available'
          )
        )
      );
    }

    return this.helpers
      .executeSearch<AutomatedIndexingScenario[]>(link, request)
      .then((searchResult) => {
        if (isApiError(searchResult)) {
          return searchResult;
        }
        if (!searchResult) searchResult = [];
        return searchResult;
      });
  };

  updateAutomatedIndexingScenario = (
    scenario: AutomatedIndexingScenario,
    request: UpdateScenarioRequestType
  ): ApiResponseAsync<AutomatedIndexingScenario> => {
    const link = scenario._links ? scenario._links['update'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "update" is not available'))
      );

    return this.helpers.executePatch(link, request);
  };

  setActivateAutomatedIndexingScenario = (
    scenario: AutomatedIndexingScenario,
    activate: boolean
  ): ApiResponseAsync<AutomatedIndexingScenario> => {
    let link = undefined;
    if (!scenario.isActive && activate && scenario._links?.['activate']) {
      link = scenario._links['activate'];
    } else if (
      scenario.isActive &&
      !activate &&
      scenario._links?.['deactivate']
    ) {
      link = scenario._links['deactivate'];
    }

    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            `link "${activate ? 'activate' : 'deactivate'}" is not available`
          )
        )
      );

    return this.helpers.handlePost(link.href, undefined);
  };

  deleteAutomatedIndexingScenario = (
    scenario: AutomatedIndexingScenario
  ): ApiResponseAsync<void> => {
    const link = scenario._links ? scenario._links['delete'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "delete scenario" is not available')
        )
      );

    return this.helpers.handleDelete(link.href);
  };

  createAutomatedIndexingScenario = (
    draft: ProcessGeneral,
    payload: CreateAutomatedIndexingScenarioRequest
  ): ApiResponseAsync<AutomatedIndexingScenario> => {
    const link = draft._links?.['createAutomatedIndexingScenario'];

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "create automated indexing scenario" is not available'
          )
        )
      );
    }
    return this.helpers.handlePost<
      CreateAutomatedIndexingScenarioRequest,
      AutomatedIndexingScenario
    >(link.href, payload);
  };

  getClassifierJobDescription = (
    draft: ProcessGeneral
  ): ApiResponseAsync<AutomatedIndexingClassifierJobDescription> => {
    const link = draft._links?.['getAutomatedIndexingClassifierJobDescription'];

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "get Automated Indexing classifier job description" is not available'
          )
        )
      );
    }
    return this.helpers.handleGet(link.href);
  };

  createTrainingTask = async (
    processInstance: BaseEntity & WithHypermedia,
    request: CreateAutomatedIndexingTrainingTaskRequest
  ): ApiResponseAsync<void> => {
    const link =
      processInstance._links?.['createAutomatedIndexingTrainingTask'];

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "create automated indexing training task" is not available for the document'
          )
        )
      );
    }

    return this.helpers.handlePostAction(link.href, request);
  };
}
